import { SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  formatFullDate,
  formatPercent,
  formatUnits,
  weekLabel,
  type PlanningPanel,
  type PlanningResult,
  type WaveWeek,
} from './model'

interface Props {
  panel: PlanningPanel
  result: PlanningResult
  year: number
  setWeek: WaveWeek | null
  /** Event code of the set-complete week (sent to the Flow Tool). */
  setEventCode?: string | null
  /** Receipt-to-sales threshold (fraction); null when the plan does not use it. */
  varianceThreshold?: number | null
  /** Recommended week for the next wave, when the plan is below 100%. */
  nextWave?: WaveWeek | null
  /** Opens the information drawer with the active parameters. */
  onShowParameters?: () => void
  /** Core plans show their receipt weeks as the headline (the minimum moves to the list). */
  core?: boolean
}

const unitsOrNA = (value: number | null) =>
  value === null ? 'N/A' : formatUnits(value)
const unitsOrDash = (value: number | null) =>
  value === null ? '—' : formatUnits(value)

export function WavePlanningSummary({
  panel,
  result,
  year,
  setWeek,
  setEventCode,
  varianceThreshold = null,
  nextWave = null,
  onShowParameters,
  core = false,
}: Props) {
  const receiptWeeks = result.weeks
    .filter(week => week.plannedWave > 0)
    .map(week => weekLabel(week.week))
  const fields = [
    [`${year} stores`, formatUnits(panel.stores)],
    [`${year} SKUs`, formatUnits(panel.skus)],
    [
      `${year - 1} stores / SKUs`,
      `${unitsOrNA(panel.previousStores)} / ${unitsOrNA(panel.previousSkus)}`,
    ],
    [
      'POG set / max qty',
      `${formatUnits(panel.pogSet)} / ${unitsOrDash(panel.pogMax)}`,
    ],
    [
      'Min / max units',
      `${formatUnits(panel.pogSet * panel.stores)} / ${unitsOrDash(panel.pogMax === null ? null : panel.pogMax * panel.stores)}`,
    ],
    ['In-season D/T', formatPercent(result.inSeasonDemandPercent, 1)],
    [
      'Avg store receipts / sales',
      `${formatUnits(result.averageStoreReceipts)} / ${formatUnits(result.averageStoreSales)}`,
    ],
    [
      'First wave · plan / SKU-calculated',
      `${formatPercent(result.plannedFirstWave, 1)} / ${result.skuFirstWave === null ? '—' : formatPercent(result.skuFirstWave, 1)}`,
    ],
    [
      'POG set minimum · SKU stores',
      result.pogSetMinimum === null
        ? '—'
        : formatPercent(result.pogSetMinimum, 1),
    ],
    ...(core
      ? [['Minimum first wave', formatPercent(result.minimumFirstWave, 1)]]
      : []),
    ['Set date · Friday', formatFullDate(panel.setDate)],
    ['Set event code', setEventCode ?? '—'],
    ...(varianceThreshold === null
      ? []
      : [
          [
            `Next wave · variance < ${formatPercent(varianceThreshold, 0)}`,
            nextWave ? `${weekLabel(nextWave.week)} · ${nextWave.event}` : '—',
          ],
        ]),
  ]

  return (
    <Card className="min-w-0 p-5 shadow-none">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Planning reference</h2>
        <span className="ml-auto text-xs text-muted-foreground">
          {[
            panel.season,
            setWeek && `${weekLabel(setWeek.week)} · ${setWeek.event}`,
          ]
            .filter(Boolean)
            .join(' · ') || '—'}
        </span>
        {onShowParameters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            aria-label="Calculation parameters"
            title="Calculation parameters"
            onClick={onShowParameters}
          >
            <SlidersHorizontal size={14} />
          </Button>
        )}
      </div>
      <div className="my-4 grid grid-cols-2 divide-x border-b pb-4">
        <div>
          <p className="text-xs text-muted-foreground">
            Planned purchase units
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {formatUnits(panel.purchaseUnits)}
          </p>
        </div>
        {core ? (
          <div className="pl-5">
            <p className="text-xs text-muted-foreground">
              Receipt weeks · initial / backup
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">
              {receiptWeeks.join(' / ') || '—'}
            </p>
          </div>
        ) : (
          <div className="pl-5">
            <p className="text-xs text-muted-foreground">Minimum first wave</p>
            <p
              className="mt-1 text-2xl font-semibold tabular-nums"
              title={formatPercent(result.minimumFirstWave, 3)}
            >
              {formatPercent(result.minimumFirstWave, 1)}
            </p>
          </div>
        )}
      </div>
      <dl className="grid grid-cols-2 gap-x-5 gap-y-2.5">
        {fields.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 text-xs font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {panel.note && (
        <p className="mt-3 border-t pt-3 text-[11px] leading-relaxed text-muted-foreground">
          {panel.note}
        </p>
      )}
    </Card>
  )
}
